// ── lib/auth.js ──
// Wraps Supabase auth + the redeem-event-code Edge Function in the named
// helpers ARCHITECTURE.md §D specifies. Throw-on-error contract; callers
// (hooks/useAuth.js, the workshop intake redeem button) just await.

import { supabase } from "./supabase.js";
import { classify } from "./intakeStoreErrors.js";

// ───────────────────────────────────────────────────────────────────
// Email/password auth (admin + attorney + account_holder)
// ───────────────────────────────────────────────────────────────────

export async function signUp(email, password, { fullName, orgId } = {}) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { full_name: fullName, org_id: orgId } },
  });
  if (error) throw classify(error, "sign up");
  return data;
}

export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw classify(error, "sign in");
  return data;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw classify(error, "sign out");
}

export async function getSession() {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw classify(error, "get session");
  return data?.session || null;
}

// ───────────────────────────────────────────────────────────────────
// Password recovery — Supabase emails a magic link that lands the user
// back on the app with `?type=recovery` in the URL hash. The Supabase
// client picks it up automatically (detectSessionInUrl: true) and fires
// onAuthStateChange with event === 'PASSWORD_RECOVERY'. The app renders
// the ResetPassword view at that point and calls updatePassword().
// ───────────────────────────────────────────────────────────────────

export async function requestPasswordReset(email, redirectTo) {
  const trimmed = (email || "").trim();
  if (!trimmed) throw new Error("Email required");
  const { error } = await supabase.auth.resetPasswordForEmail(trimmed, {
    redirectTo: redirectTo || `${window.location.origin}/`,
  });
  if (error) throw classify(error, "request password reset");
}

export async function updatePassword(newPassword) {
  if (!newPassword || newPassword.length < 8) {
    throw new Error("Password must be at least 8 characters");
  }
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw classify(error, "update password");
}

// ───────────────────────────────────────────────────────────────────
// Workshop guest flow — calls the redeem-event-code Edge Function
//
// On success:
//   - The function returns { event_id, session: { access_token, refresh_token } }.
//   - We hand the session to supabase.auth.setSession() so all subsequent
//     RLS-checked reads/writes run as the new anon auth.uid().
// ───────────────────────────────────────────────────────────────────

export async function signInAsWorkshopGuest(eventCode) {
  const code = (eventCode || "").trim().toUpperCase();
  if (!code) throw new Error("event code required");

  const { data, error } = await supabase.functions.invoke("redeem-event-code", {
    body: { event_code: code },
  });
  if (error) throw classify(error, "redeem event code");

  // Function returns { error: '...' } as a non-2xx body for known cases.
  if (data?.error) {
    if (data.error === "code_not_recognized") {
      throw new Error("Code not recognized. Check the access code and try again.");
    }
    if (data.error === "event_closed") {
      throw new Error("This event is closed. Please contact your event organizer.");
    }
    throw new Error(`Unable to redeem code (${data.error})`);
  }

  if (!data?.session?.access_token) throw new Error("Edge function returned no session");

  const { error: setErr } = await supabase.auth.setSession(data.session);
  if (setErr) throw classify(setErr, "establish guest session");

  return { eventId: data.event_id };
}

// ───────────────────────────────────────────────────────────────────
// Cross-device draft resume — calls the resume-draft Edge Function
// ───────────────────────────────────────────────────────────────────

export async function resumeDraft(eventCode, resumeToken) {
  const code  = (eventCode || "").trim().toUpperCase();
  const token = (resumeToken || "").trim();
  if (!code || !token) throw new Error("event code and resume token required");

  const { data, error } = await supabase.functions.invoke("resume-draft", {
    body: { event_code: code, resume_token: token },
  });
  if (error) throw classify(error, "resume draft");

  if (data?.error) {
    if (data.error === "draft_not_found") {
      throw new Error("We couldn't find a saved draft with that code.");
    }
    if (data.error === "rate_limited") {
      throw new Error("Too many attempts. Please wait a minute and try again.");
    }
    throw new Error(`Unable to resume draft (${data.error})`);
  }

  if (!data?.session?.access_token) throw new Error("Edge function returned no session");

  const { error: setErr } = await supabase.auth.setSession(data.session);
  if (setErr) throw classify(setErr, "establish resume session");

  return {
    draftId: data.draft_id,
    eventId: data.event_id,
    payload: data.payload || {},
  };
}
